import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Tag } from "lucide-react";
import { useCreatePricingRule } from "@/hooks/useProducts";

const ruleTypes = [
  { value: "discount", label: "Discount" },
  { value: "markup", label: "Markup" },
  { value: "volume", label: "Volume Discount" },
  { value: "bundle", label: "Bundle Price" },
];

export function AddGlobalPricingRuleDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [ruleType, setRuleType] = useState("discount");
  const [value, setValue] = useState("");
  const [valueType, setValueType] = useState("percentage");
  const [minQuantity, setMinQuantity] = useState("");
  const [maxQuantity, setMaxQuantity] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [priority, setPriority] = useState("0");
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");

  const createRule = useCreatePricingRule();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const condition: Record<string, number> = {};
    if (minQuantity) condition.min_quantity = parseInt(minQuantity);
    if (maxQuantity) condition.max_quantity = parseInt(maxQuantity);

    await createRule.mutateAsync({
      product_id: null, // Global rule
      name,
      rule_type: ruleType,
      value: parseFloat(value) || 0,
      value_type: valueType,
      condition,
      priority: parseInt(priority) || 0,
      is_active: isActive,
      valid_from: validFrom || null,
      valid_until: validUntil || null,
      metadata: {},
    });

    resetForm();
    setOpen(false);
  };

  const resetForm = () => {
    setName("");
    setRuleType("discount");
    setValue("");
    setValueType("percentage");
    setMinQuantity("");
    setMaxQuantity("");
    setIsActive(true);
    setPriority("0");
    setValidFrom("");
    setValidUntil("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Global Rule
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-primary" />
            Add Global Pricing Rule
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ruleName">Rule Name *</Label>
            <Input
              id="ruleName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Summer Sale Discount"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Rule Type</Label>
              <Select value={ruleType} onValueChange={setRuleType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ruleTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Value Type</Label>
              <Select value={valueType} onValueChange={setValueType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="fixed">Fixed Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="value">Value *</Label>
              <Input
                id="value"
                type="number"
                step="0.01"
                min="0"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={valueType === "percentage" ? "10" : "100"}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Input
                id="priority"
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">Higher = applied first</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="minQty">Min Quantity</Label>
              <Input
                id="minQty"
                type="number"
                min="0"
                value={minQuantity}
                onChange={(e) => setMinQuantity(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxQty">Max Quantity</Label>
              <Input
                id="maxQty"
                type="number"
                min="0"
                value={maxQuantity}
                onChange={(e) => setMaxQuantity(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="validFrom">Valid From</Label>
              <Input
                id="validFrom"
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="validUntil">Valid Until</Label>
              <Input
                id="validUntil"
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch id="ruleActive" checked={isActive} onCheckedChange={setIsActive} />
            <Label htmlFor="ruleActive">Active</Label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createRule.isPending}>
              {createRule.isPending ? "Creating..." : "Create Rule"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
